import { useEffect, useState } from "react";
import { Euro, Package, ShoppingCart, GraduationCap, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "../../lib/api";
import { useLang } from "../../i18n";

const CAT_COLORS = { mieli: "#F59E0B", polline: "#D97706", propoli: "#9A3412", regalo: "#84CC16" };

export default function Dashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="font-mono text-sm text-slate-400" data-testid="dashboard-loading">{t("common.loading")}</p>;

  const cards = [
    { label: t("admin.revenue"), value: `€${stats.revenue.toFixed(2)}`, icon: Euro, testid: "stat-revenue" },
    { label: t("admin.tofulfill"), value: stats.orders_to_fulfill, icon: ShoppingCart, testid: "stat-tofulfill" },
    { label: t("admin.jars"), value: stats.jars_sold, icon: Package, testid: "stat-jars" },
    { label: t("admin.bookings"), value: stats.bookings, icon: GraduationCap, testid: "stat-bookings" },
    { label: t("admin.customers"), value: stats.customers, icon: Users, testid: "stat-customers" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl font-bold" data-testid="dashboard-title">{t("admin.dashboard")}</h1>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(({ label, value, icon: Icon, testid }) => (
          <div key={testid} className="bg-slate2 border border-slate-700 rounded-lg p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
              <Icon size={15} className="text-gold" />
            </div>
            <p className="mt-3 font-mono text-2xl font-semibold text-slate-50">{value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-slate2 border border-slate-700 rounded-lg p-6" data-testid="sales-by-category-chart">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-5">{t("admin.sales.by.cat")}</p>
          {stats.sales_by_category.length === 0 ? (
            <p className="text-slate-500 text-sm py-10 text-center">—</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.sales_by_category}>
                <XAxis dataKey="category" tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", color: "#F8FAFC" }} cursor={{ fill: "rgba(245,158,11,0.08)" }} />
                <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                  {stats.sales_by_category.map((e) => <Cell key={e.category} fill={CAT_COLORS[e.category] || "#F59E0B"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-slate2 border border-slate-700 rounded-lg p-6" data-testid="recent-orders-table">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-5">{t("admin.recent")}</p>
          <div className="space-y-3">
            {stats.recent_orders.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">—</p>}
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-b border-slate-700/60 pb-3" data-testid={`recent-order-${o.id}`}>
                <div>
                  <p className="font-medium text-slate-200">{o.customer?.name}</p>
                  <p className="font-mono text-xs text-slate-500">#{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString("it-IT")}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-gold">€{o.total.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">{t(`order.status.${o.status}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
